import { Component, OnInit, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ParkingSpaceListComponent } from "./parking-space-list/parking-space-list.component";
import { CreateParkingSpaceComponent } from "./create-parking-space/create-parking-space.component";
import { ParkingSpaceDto, ParkingSpaceDtoPaginationResult, ParkingSpaceService, ParkingSpaceType } from '../../api/gen';
import { inject } from '@angular/core';
import { PaginationConfig } from '../models/config/pagination-config';
import { PageEvent } from '@angular/material/paginator';
// Import CommonModule for now until we can properly build the shared library
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JjamesButtonComponent, JJamesCardComponent, ResponsiveBaseComponent, Role } from 'shared-lib';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ParkingSpaceFilter, ParkingSpacesFilterComponent } from './parking-spaces-filter/parking-spaces-filter.component';
import { RoleService } from '../auth/role-service';

@Component({
  selector: 'app-parking-space',
  imports: [
    ParkingSpaceListComponent,
    CommonModule,
    FormsModule,
    JjamesButtonComponent,
    JJamesCardComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './parking-space.component.html',
  styleUrl: './parking-space.component.scss'
})
export class ParkingSpaceComponent extends ResponsiveBaseComponent implements OnInit {
  private roleService = inject(RoleService);

  searchTerm = signal('');
  filteredSpaces = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const items = this.parkingSpaces()?.items ?? [];
    if (!term) return items;
    return items.filter(s => s.name?.toLowerCase().includes(term));
  });
  parkingSpaces = signal<ParkingSpaceDtoPaginationResult | null>(null);
  pagination: PaginationConfig | null = new PaginationConfig(20, 0, 0);
  isLoading: boolean = false;
  currentFilter: ParkingSpaceFilter = {};
  isAdmin = signal(false);

  constructor(
    private readonly parkingSpaceService: ParkingSpaceService,
    private readonly router: Router,
    private readonly dialog: MatDialog
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.loadParkingSpaces();
    this.isAdmin.set(await this.roleService.checkAccess(Role.Administrator));
  }

  onSearch(value: string): void {
    this.searchTerm.set(value);
  }

  clearSearch(): void {
    this.searchTerm.set('');
  }

  private loadParkingSpaces() {
    this.isLoading = true;
    //    public parkingSpaceFilterPost(name?: string, type?: ParkingSpaceType, parkingZoneId?: string, parkingSpaceInfrastructureId?: string, pageIndex?: number, pageSize?: number, sortBy?: string, sortOrder?: SortOrder, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'text/plain' | 'application/json' | 'text/json', context?: HttpContext, transferCache?: boolean}): Observable<any> {

    this.parkingSpaceService.parkingSpaceFilterPost(
      this.currentFilter.name,
      this.currentFilter.type || ParkingSpaceType.Standard,
      this.currentFilter.parkingZoneId,
      this.currentFilter.parkingSpaceInfrastructureId,
      this.currentFilter.accessMode,
      this.currentFilter.entryType,
      this.pagination?.currentPage,
      this.pagination?.itemsPerPage,
      this.currentFilter.sortBy,
      this.currentFilter.sortOrder
    ).subscribe({
      next: parkingSpaces => {
        this.parkingSpaces.set(parkingSpaces ?? {});
        // Update pagination total items
        this.pagination = new PaginationConfig(
          this.pagination?.itemsPerPage ?? 20,
          this.pagination?.currentPage ?? 0,
          parkingSpaces?.totalCount ?? 0
        );
        this.isLoading = false;
      },
      error: err => {
        console.error('Error loading parking spaces', err);
        this.isLoading = false;
      }
    });
  }

  onPageChange($event: PageEvent) {
    this.pagination = new PaginationConfig($event.pageSize, $event.pageIndex, this.parkingSpaces()?.totalCount ?? 0);
    this.loadParkingSpaces();
  }

  onParkingSpaceDeleted(parkingSpaceId: string) {
    // Reload the list after deletion
    this.loadParkingSpaces();
  }

  onCreateParkingArea() {
    // Navigate to the create parking space component as a separate route
    this.router.navigate(['/parking-space/new']);
  }

  openFilterDialog(): void {
    const dialogRef = this.dialog.open(ParkingSpacesFilterComponent, {
      width: this.isHandset() ? '95vw' : '600px',
      maxWidth: this.isHandset() ? '95vw' : '600px',
      data: this.currentFilter,
      panelClass: 'filter-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.currentFilter = result;
        // Reset to first page when applying new filters
        this.pagination = new PaginationConfig(
          this.pagination?.itemsPerPage ?? 20,
          0,
          0
        );
        this.loadParkingSpaces();
      }
    });
  }
}
